import { useState } from "react"
import { Helmet } from "react-helmet-async"
import { Link, useNavigate, useLocation } from "react-router-dom"
import { loginUser } from "../services/userService"
import { acceptInvitation } from "../services/chaperonInvitationsConnector"
import { fetchCommunityPlaces } from "../utilities/communities"
import "../css/user-registration.css"
import { consoleLog, buildFullName } from "../utilities/helpers"

export default function UserLogin() {
    const navigate = useNavigate()
    const location = useLocation()
    const [formData, setFormData] = useState({
        username: "",
        password: "",
        rememberMe: false,
    })
    const [errors, setErrors] = useState({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [loginSuccess, setLoginSuccess] = useState(false)

    // Get the redirect path from location state, or default to dashboard
    const from = location.state?.from?.pathname || "/community"
    const invitationToken = location.state?.invitationToken || null
    const invitationCommunityName = location.state?.communityName || null

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }))

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: "",
            }))
        }
    }

    const validateForm = () => {
        const newErrors = {}

        if (!formData.username.trim()) {
            newErrors.username = "Username is required"
        }

        if (!formData.password) {
            newErrors.password = "Password is required"
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        if (!validateForm()) {
            return
        }

        setIsSubmitting(true)
        setErrors({})

        try {
            const credentials = {
                username: formData.username.trim(),
                password: formData.password,
            }

            const response = await loginUser(credentials)

            if (response.status === "success") {
                setLoginSuccess(true)
                consoleLog("Login successful:", response.user)

                // Tracks whether an invitation was auto-accepted below; declared
                // here (not inside the `if (response.user)` block) so it's still
                // in scope when welcomeMessage is computed further down.
                let invitationAccepted = false

                // Store user data in localStorage for quick access
                if (response.user) {
                    localStorage.setItem(
                        "chaperon_user_data",
                        JSON.stringify({
                            id: response.user.id,
                            email: response.user.email,
                            username: response.user.username,
                            first_name: response.user.first_name,
                            last_name: response.user.last_name,
                            full_name: buildFullName(
                                response.user.first_name,
                                response.user.last_name,
                            ),
                            profile_picture_url:
                                response.user.profile_picture_url,
                            status: response.user.status,
                            role: response.user.role,
                            subscription_status:
                                response.user.subscription_status,
                            subscription_plan: response.user.subscription_plan,
                            metadata: response.user.metadata,
                        }),
                    )

                    // If the user arrived via an invitation link, accept it first
                    // so that the community cache reflects the updated membership
                    if (invitationToken && response.user.id) {
                        try {
                            await acceptInvitation({
                                invitation_token: invitationToken,
                                user_id: response.user.id,
                            })
                            invitationAccepted = true
                        } catch (inviteErr) {
                            // Non-fatal - log and continue to dashboard
                            // TODO: Consider showing a notification about the failed auto-accept and how to resolve it
                            console.error(
                                "Failed to auto-accept invitation after login:",
                                inviteErr,
                            )
                        }
                    }

                    // Fetch, enrich and store user's communities (after any invitation
                    // has been accepted so the cache reflects current membership)
                    if (response.user.id) {
                        consoleLog(
                            "Fetching and enriching communities for user ID:",
                            response.user.id,
                        )
                        try {
                            // fetchCommunityPlaces will handle fetching from API,
                            // enriching with entity data, and storing to localStorage
                            await fetchCommunityPlaces(response.user.id)
                        } catch (error) {
                            console.error(
                                "Error fetching user communities:",
                                error,
                            )
                            // Continue with login even if communities fetch fails
                            // fetchCommunityPlaces returns [] on error, but also store empty response
                            localStorage.setItem(
                                "chaperon_user_communities",
                                JSON.stringify({ communities: [] }),
                            )
                        }
                    } else {
                        // No user ID available, store empty communities
                        localStorage.setItem(
                            "chaperon_user_communities",
                            JSON.stringify({ communities: [] }),
                        )
                    }
                }

                // Notify other components about auth state change
                window.dispatchEvent(new Event("authChange"))

                const welcomeMessage =
                    invitationAccepted && invitationCommunityName
                        ? `Welcome back! You've joined ${invitationCommunityName}.`
                        : "Welcome back! You've successfully logged in."

                // TODO: When implementing JWT + HTTP-only cookies:
                // - Remove localStorage token storage from userService.loginUser()
                // - Server will set HTTP-only cookie in response
                // - Add credentials: 'include' to fetch requests
                // - Implement refresh token rotation
                // - Add CSRF protection

                setTimeout(() => {
                    navigate(invitationToken ? "/community" : from, {
                        state: {
                            message: welcomeMessage,
                            user: response.user,
                        },
                        replace: true,
                    })
                }, 1500)
            }
        } catch (error) {
            console.error("Login error:", error)

            if (error.response?.data?.message) {
                const message = error.response.data.message

                // Handle specific error types
                if (
                    message.toLowerCase().includes("username") ||
                    message.toLowerCase().includes("not found")
                ) {
                    setErrors({ username: message })
                } else if (
                    message.toLowerCase().includes("password") ||
                    message.toLowerCase().includes("incorrect") ||
                    message.toLowerCase().includes("invalid")
                ) {
                    setErrors({ password: message })
                } else {
                    setErrors({ general: message })
                }
            } else {
                setErrors({
                    general: "An unexpected error occurred. Please try again.",
                })
            }
        } finally {
            setIsSubmitting(false)
        }
    }

    if (loginSuccess) {
        return (
            <div className="auth-success-container">
                <Helmet>
                    <title>Login Successful - Chaperon</title>
                </Helmet>

                <div className="auth-success-card">
                    <div className="mb-4 success-animation">
                        <i
                            className="bi bi-check-circle-fill text-success"
                            style={{ fontSize: "4rem" }}
                        ></i>
                    </div>
                    <h2 className="mb-3">Welcome Back!</h2>
                    <p className="mb-4">
                        You've successfully logged in. Redirecting you now...
                    </p>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="auth-container">
            <Helmet>
                <title>Welcome Back - Chaperon</title>
                <meta
                    name="description"
                    content="Sign in to your Chaperon account to access your safety dashboard and personalized travel insights."
                />
            </Helmet>

            {/* Left Side - Brand/Image Section */}
            <div
                className="auth-image-section"
                style={{
                    backgroundImage: "url(/image/galina-mountain-hike.jpg)",
                }}
            >
                <div className="auth-brand-content">
                    <h1>Welcome Back</h1>
                    <p>
                        Continue your journey with comprehensive safety insights
                        and real-time alerts for destinations worldwide.
                    </p>
                </div>
            </div>

            {/* Right Side - Login Form */}
            <div className="auth-form-section">
                <div className="auth-form-container">
                    <div className="auth-form-header">
                        <h1>Sign In</h1>
                        <p>Sign in to your Chaperon account</p>
                    </div>

                    {errors.general && (
                        <div className="alert alert-danger" role="alert">
                            {errors.general}
                        </div>
                    )}

                    <form
                        onSubmit={handleSubmit}
                        noValidate
                        className="auth-form"
                    >
                        <div className="mb-3">
                            <input
                                type="text"
                                className={`form-control ${
                                    errors.username ? "is-invalid" : ""
                                }`}
                                id="username"
                                name="username"
                                value={formData.username}
                                onChange={handleInputChange}
                                placeholder="Username"
                                required
                                autoComplete="username"
                            />
                            {errors.username && (
                                <div className="invalid-feedback">
                                    {errors.username}
                                </div>
                            )}
                        </div>

                        <div className="mb-3">
                            <input
                                type="password"
                                className={`form-control ${
                                    errors.password ? "is-invalid" : ""
                                }`}
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                placeholder="Password"
                                required
                                autoComplete="current-password"
                            />
                            {errors.password && (
                                <div className="invalid-feedback">
                                    {errors.password}
                                </div>
                            )}
                        </div>

                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="rememberMe"
                                    name="rememberMe"
                                    checked={formData.rememberMe}
                                    onChange={handleInputChange}
                                />
                                <label
                                    className="form-check-label"
                                    htmlFor="rememberMe"
                                >
                                    Remember me
                                </label>
                            </div>
                            <Link
                                to="/user/forgot-password"
                                className="auth-link"
                            >
                                Forgot Password?
                            </Link>
                        </div>

                        <button
                            type="submit"
                            className="btn-auth-primary"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <span
                                        className="spinner-border spinner-border-sm me-2"
                                        role="status"
                                        aria-hidden="true"
                                    ></span>
                                    Signing In...
                                </>
                            ) : (
                                "Sign In"
                            )}
                        </button>
                    </form>

                    <div className="auth-divider">
                        <span>Don't have an account?</span>
                    </div>

                    <div className="text-center">
                        <Link
                            to="/user/register"
                            state={location.state}
                            className="auth-link"
                        >
                            Create one here
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    )
}
